import { useCallback, useEffect, useState } from 'react';
import { useSchedule } from '../context/ScheduleContext';
import { CapacityBar } from './GroupCapacityBadge';
import type { GroupSession, GroupEnrollment, EnrollmentStatus } from './groupTypes';
import type { Client } from '../types';

// ─── Status chip ───────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<EnrollmentStatus, { label: string; color: string; bg: string }> = {
  enrolled:   { label: 'Enrolled',   color: '#2563eb', bg: '#eff6ff' },
  waitlisted: { label: 'Waitlisted', color: '#d97706', bg: '#fffbeb' },
  attended:   { label: 'Attended',   color: '#16a34a', bg: '#f0fdf4' },
  no_show:    { label: 'No-show',    color: '#dc2626', bg: '#fef2f2' },
  cancelled:  { label: 'Cancelled',  color: '#94a3b8', bg: '#f8fafc' },
};

function StatusChip({ status }: { status: EnrollmentStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.enrolled;
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.color}30`,
        borderRadius: 4,
        padding: '1px 5px',
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.label}
    </span>
  );
}

// ─── Roster row ────────────────────────────────────────────────────────────

function RosterRow({ entry, position }: { entry: GroupEnrollment; position: number }) {
  const enrolledDate = entry.enrolledAt
    ? new Date(entry.enrolledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 10px',
        borderBottom: '1px solid #f1f5f9',
        background: '#fff',
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: '#e2e8f0',
          color: '#64748b',
          fontSize: 10,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {position}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.displayLabel}
          {entry.waitlistPosition !== undefined && (
            <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>
              #{entry.waitlistPosition}
            </span>
          )}
        </div>
        {enrolledDate && (
          <div style={{ fontSize: 10, color: '#94a3b8' }}>Enrolled {enrolledDate}</div>
        )}
      </div>
      <StatusChip status={entry.status} />
    </div>
  );
}

// ─── Enroll patient button + picker ───────────────────────────────────────

function EnrollArea({
  session,
  eventId,
  clients,
  onEnroll,
}: {
  session: GroupSession;
  eventId: string;
  clients: Client[];
  onEnroll: (eventId: string, session: GroupSession, client: Client) => void;
}) {
  const enrolledIds = new Set((session.roster ?? []).map((r) => r.patientId));
  const eligible = clients.filter((c) => !enrolledIds.has(c.id));
  const [selectedId, setSelectedId] = useState('');
  const selected = eligible.find((c) => c.id === selectedId);
  const spotsLeft = Math.max(0, session.capacity - session.enrolledCount);
  const canEnroll = selected !== undefined;
  const buttonLabel = !session.isOpen
    ? 'Group Closed'
    : spotsLeft === 0
      ? `Add ${selected?.name ?? 'Patient'} to Waitlist`
      : `Enroll ${selected?.name ?? 'Patient'}  ·  ${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`;

  return (
    <div
      style={{
        padding: '10px 10px 8px',
        borderTop: '1px solid #e2e8f0',
        background: '#f8fafc',
        flexShrink: 0,
      }}
    >
      {clients.length > 0 ? (
        <>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            style={{
              width: '100%',
              padding: '5px 8px',
              fontSize: 12,
              border: '1px solid #cbd5e1',
              borderRadius: 5,
              background: '#fff',
              color: '#1e293b',
              marginBottom: 7,
              outline: 'none',
            }}
          >
            <option value="">Select patient to enroll…</option>
            {eligible.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <button
            onClick={() => selected && onEnroll(eventId, session, selected)}
            disabled={!canEnroll || !session.isOpen && spotsLeft > 0}
            style={{
              width: '100%',
              padding: '7px 0',
              fontSize: 12,
              fontWeight: 600,
              color: canEnroll ? '#fff' : '#94a3b8',
              background: canEnroll ? '#2563eb' : '#e2e8f0',
              border: 'none',
              borderRadius: 6,
              cursor: canEnroll ? 'pointer' : 'not-allowed',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => {
              if (canEnroll) (e.currentTarget as HTMLButtonElement).style.background = '#1d4ed8';
            }}
            onMouseLeave={(e) => {
              if (canEnroll) (e.currentTarget as HTMLButtonElement).style.background = '#2563eb';
            }}
          >
            {buttonLabel}
          </button>
        </>
      ) : (
        <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', padding: '4px 0' }}>
          No patient pool configured.
        </div>
      )}
    </div>
  );
}

// ─── Attendance summary bar ────────────────────────────────────────────────

function AttendanceSummary({
  summary,
}: {
  summary: { enrolled: number; attended: number; noShow: number; cancelled: number };
}) {
  const total = summary.enrolled + summary.attended + summary.noShow + summary.cancelled;
  if (total === 0) return null;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr',
        gap: 1,
        borderBottom: '1px solid #e2e8f0',
        flexShrink: 0,
      }}
    >
      {[
        { label: 'Enrolled', value: summary.enrolled, color: '#2563eb' },
        { label: 'Attended', value: summary.attended, color: '#16a34a' },
        { label: 'No-show',  value: summary.noShow,   color: '#dc2626' },
        { label: 'Cancelled',value: summary.cancelled, color: '#94a3b8' },
      ].map(({ label, value, color }) => (
        <div
          key={label}
          style={{
            padding: '6px 4px',
            textAlign: 'center',
            background: '#fff',
            borderRight: '1px solid #f1f5f9',
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
          <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Group info header ─────────────────────────────────────────────────────

const FACILITY_LABEL: Record<string, string> = {
  in_person: 'In-person',
  telehealth: 'Telehealth',
  hybrid: 'Hybrid',
};

function SessionInfoHeader({ session }: { session: GroupSession }) {
  return (
    <div style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
        {session.groupName}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 8px', marginBottom: 6 }}>
        <span style={{ fontSize: 10, color: '#64748b' }}>
          <b style={{ color: '#475569' }}>{session.groupType}</b>
        </span>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>·</span>
        <span style={{ fontSize: 10, color: '#64748b' }}>
          {FACILITY_LABEL[session.facilityType] ?? session.facilityType}
        </span>
        {session.location && (
          <>
            <span style={{ fontSize: 10, color: '#94a3b8' }}>·</span>
            <span style={{ fontSize: 10, color: '#64748b' }}>{session.location}</span>
          </>
        )}
      </div>
      <CapacityBar session={session} />
      {session.waitlistCount > 0 && (
        <div style={{ fontSize: 10, color: '#d97706', marginTop: 3 }}>
          {session.waitlistCount} patient{session.waitlistCount !== 1 ? 's' : ''} on group waitlist
        </div>
      )}
    </div>
  );
}

// ─── Main panel ────────────────────────────────────────────────────────────

export function GroupRosterPanel() {
  const { groupTherapyHandle, selectedEvent, config } = useSchedule();
  const clients = (config.clients ?? []) as Client[];
  const { sessionsMap, attendanceData, attendanceLoading, requestAttendance, clearAttendance, startEnrollment } = groupTherapyHandle;

  const [activeEventId, setActiveEventId] = useState<string | null>(null);

  // Auto-load attendance when a group session event is selected.
  useEffect(() => {
    if (!selectedEvent) return;
    const session = sessionsMap.get(selectedEvent.id);
    if (!session) return;
    if (activeEventId === selectedEvent.id) return;

    setActiveEventId(selectedEvent.id);
    clearAttendance();
    requestAttendance({ eventId: selectedEvent.id, groupId: session.groupId });
  }, [selectedEvent, sessionsMap, activeEventId, requestAttendance, clearAttendance]);

  const handleEnroll = useCallback((eventId: string, session: GroupSession, client: Client) => {
    startEnrollment({
      eventId,
      groupId: session.groupId,
      groupName: session.groupName,
      patientId: client.id,
      displayLabel: client.name,
      start: selectedEvent?.startUtc ?? '',
      end: selectedEvent?.endUtc ?? '',
      resourceIds: session.facilitatorIds ?? [],
    });
  }, [startEnrollment, selectedEvent]);

  // Determine which session to show.
  const currentEventId = activeEventId ?? selectedEvent?.id;
  const currentSession = currentEventId ? sessionsMap.get(currentEventId) : undefined;

  if (!groupTherapyHandle.enabled) {
    return (
      <div style={{ padding: '20px 12px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
        Group therapy module not enabled.
      </div>
    );
  }

  if (!currentSession) {
    // Show list of all group sessions in the current view.
    const allSessions = [...sessionsMap.entries()];
    if (allSessions.length === 0) {
      return (
        <div style={{ padding: '20px 12px', fontSize: 12, color: '#94a3b8', textAlign: 'center', lineHeight: 1.6 }}>
          No group sessions in the current view.
          <br />Click a group session event to see the roster.
        </div>
      );
    }

    return (
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <div style={{ padding: '8px 10px', fontSize: 11, color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>
          {allSessions.length} GROUP SESSION{allSessions.length !== 1 ? 'S' : ''} THIS WEEK
        </div>
        {allSessions.map(([eventId, session]) => (
          <div
            key={eventId}
            onClick={() => {
              setActiveEventId(eventId);
              clearAttendance();
              requestAttendance({ eventId, groupId: session.groupId });
            }}
            style={{
              padding: '10px',
              borderBottom: '1px solid #f1f5f9',
              cursor: 'pointer',
              background: '#fff',
              transition: 'background 0.1s',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#fff'; }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{session.groupName}</div>
            <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4 }}>
              {session.groupType} · {FACILITY_LABEL[session.facilityType] ?? session.facilityType}
            </div>
            <CapacityBar session={session} />
          </div>
        ))}
      </div>
    );
  }

  const roster = attendanceData?.roster ?? currentSession.roster ?? [];
  const enrolled = roster.filter((r) => r.status === 'enrolled' || r.status === 'attended' || r.status === 'no_show');
  const waitlisted = roster.filter((r) => r.status === 'waitlisted');

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Back button when browsing from the list */}
      {!selectedEvent && (
        <button
          onClick={() => { setActiveEventId(null); clearAttendance(); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px',
            background: '#f1f5f9', border: 'none', borderBottom: '1px solid #e2e8f0',
            cursor: 'pointer', fontSize: 11, color: '#64748b', width: '100%', textAlign: 'left',
          }}
        >
          ← All sessions
        </button>
      )}

      <SessionInfoHeader session={currentSession} />

      {attendanceData && (
        <AttendanceSummary summary={attendanceData.attendanceSummary} />
      )}

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {attendanceLoading ? (
          <div style={{ padding: '20px 12px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
            Loading roster…
          </div>
        ) : roster.length === 0 ? (
          <div style={{ padding: '20px 12px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
            No patients enrolled yet.
          </div>
        ) : (
          <>
            {enrolled.length > 0 && (
              <>
                <div style={{ padding: '5px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', letterSpacing: '0.05em' }}>
                  ROSTER ({enrolled.length})
                </div>
                {enrolled.map((entry, i) => (
                  <RosterRow key={entry.patientId} entry={entry} position={i + 1} />
                ))}
              </>
            )}
            {waitlisted.length > 0 && (
              <>
                <div style={{ padding: '5px 10px', fontSize: 10, fontWeight: 700, color: '#d97706', background: '#fffbeb', borderBottom: '1px solid #fef3c7', letterSpacing: '0.05em' }}>
                  WAITLIST ({waitlisted.length})
                </div>
                {waitlisted
                  .sort((a, b) => (a.waitlistPosition ?? 99) - (b.waitlistPosition ?? 99))
                  .map((entry, i) => (
                    <RosterRow key={entry.patientId} entry={entry} position={i + 1} />
                  ))}
              </>
            )}
          </>
        )}
      </div>

      <EnrollArea
        session={currentSession}
        eventId={currentEventId!}
        clients={clients}
        onEnroll={handleEnroll}
      />
    </div>
  );
}
